import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { PhoneIcon, Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import Logo from './Logo';
import GoogleReviews from './GoogleReviews';
import Button from './ui/Button';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const { language, translations, setLanguage } = useLanguage();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Close the language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Helper to get typed keys safely if needed, but here we trust the type
  const getLanguageLabel = (lang: string) => lang.toUpperCase();

  const changeLanguage = (newLang: 'de' | 'en' | 'es' | 'ru') => {
    setLanguage(newLang);
    setIsLangDropdownOpen(false);
  };

  const navLinks = [
    { to: '/', label: translations.nav.home },
    { to: '/implantate', label: translations.nav.implants },
    { to: '/veneers', label: translations.nav.veneers },
    { to: '/technologien', label: translations.nav.technology },
    { to: '/kontakt', label: translations.nav.contact },
  ];

  return (
    <>
      <header className="fixed w-full bg-white/90 backdrop-blur-md shadow-soft z-40 transition-all duration-300">
        {/* Top Bar for Social Proof */}
        <div className="bg-navy text-white text-xs py-1.5 hidden md:block">
          <div className="container-custom flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <a href="tel:+34603319648" className="hover:text-mint transition-colors flex items-center">
                <PhoneIcon className="w-3 h-3 mr-1.5 text-mint" />
                {translations.header.phone}
              </a>
              <a href="https://wa.me/+34603319648" className="hover:text-mint transition-colors flex items-center">
                <svg className="w-3 h-3 mr-1.5 fill-current text-mint" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                {translations.header.whatsapp}
              </a>
            </div>
            <div className="flex items-center space-x-4 opacity-90">
              <span>Avenida de Jaime III, № 3, Palma</span>
              <GoogleReviews />
            </div>
          </div>
        </div>

        <div className="container-custom py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 z-40 relative">
              <Logo className="h-10 md:h-12 w-auto transition-transform hover:scale-105" color="#93cbb6" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1 xl:space-x-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-3 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${location.pathname === link.to
                    ? 'text-navy bg-mint/10'
                    : 'text-navy hover:text-mint hover:bg-gray-50'
                    }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/emergency"
                className="flex items-center px-3 py-2 text-coral font-bold hover:bg-coral/10 rounded-md transition-all animate-pulse-slow"
              >
                <PhoneIcon className="w-4 h-4 mr-1.5" />
                {translations.nav.emergency}
              </Link>
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center space-x-3">
              {/* Language Selector */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                  className="flex items-center space-x-1 px-3 py-2 rounded-full border border-gray-200 hover:border-mint hover:bg-gray-50 transition-all text-sm font-semibold text-navy"
                >
                  <span>{getLanguageLabel(language)}</span>
                  <ChevronDownIcon className={`w-3 h-3 transition-transform ${isLangDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isLangDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-32 bg-white rounded-xl shadow-premium border border-gray-100 overflow-hidden py-1 animate-scaleUp">
                    {['de', 'en', 'es', 'ru'].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => changeLanguage(lang as any)}
                        className={`block w-full text-left px-4 py-2 text-sm transition-colors ${language === lang
                          ? 'bg-mint/20 text-navy font-bold'
                          : 'text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        {lang === 'de' && 'Deutsch'}
                        {lang === 'en' && 'English'}
                        {lang === 'es' && 'Español'}
                        {lang === 'ru' && 'Русский'}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Button as={Link} to="/kontakt?booking=true" variant="primary" size="sm" className="shadow-lg hover:shadow-mint/50">
                {translations.nav.book}
              </Button>
            </div>

            {/* Mobile Actions */}
            <div className="lg:hidden flex items-center space-x-2">
              <a href="tel:+34603319648" className="p-2 text-navy hover:text-mint bg-gray-50 rounded-full">
                <PhoneIcon className="w-5 h-5" />
              </a>

              <button
                className="p-2 text-navy focus:outline-none z-40 relative"
                onClick={() => setIsMenuOpen(true)}
                aria-label="Open menu"
              >
                <Bars3Icon className="w-7 h-7" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Pro Max Mobile Menu Overlay - z-50 to cover EVERYTHING including header */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-white/95 backdrop-blur-xl"></div>

        {/* Menu Content */}
        <div className={`relative h-full flex flex-col p-6 transition-all duration-500 ease-out transform ${isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          {/* Mobile Header with Close Button */}
          <div className="flex justify-between items-center mb-6">
            <Logo className="h-10 w-auto" color="#0a192f" />
            <button
              className="p-2 text-navy hover:text-coral transition-colors bg-gray-50 rounded-full shadow-sm"
              onClick={() => setIsMenuOpen(false)}
              aria-label="Close menu"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Pro Max Info Card - Mirrored from Top Bar */}
          <div className="bg-navy rounded-xl p-4 mb-6 text-white shadow-soft animate-fadeIn">
            <div className="flex flex-col space-y-3">
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <a href="tel:+34603319648" className="flex items-center text-sm font-bold hover:text-mint transition-colors">
                  <PhoneIcon className="w-4 h-4 mr-2 text-mint" />
                  {translations.header.phone}
                </a>
                <a href="https://wa.me/+34603319648" className="flex items-center text-sm font-bold hover:text-mint transition-colors">
                  <svg className="w-4 h-4 mr-2 fill-current text-mint" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                  {translations.header.whatsapp}
                </a>
              </div>
              <div className="text-xs text-center opacity-90 pt-1">
                <p className="mb-1">Avenida de Jaime III, № 3, Palma</p>
                <div className="flex justify-center">
                  <div className="bg-white/10 px-2 py-0.5 rounded text-[10px] tracking-wider uppercase font-bold text-mint">
                    {['de', 'en', 'es', 'ru'].includes(language) ? 'Exceptional' : 'Excelente'} ★★★★★
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-col space-y-2 flex-grow overflow-y-auto">
            {navLinks.map((link, index) => (
              <Link
                key={link.to}
                to={link.to}
                className="group flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-all active:scale-[0.98]"
                onClick={() => setIsMenuOpen(false)}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <span className={`text-xl font-bold font-title group-hover:text-mint transition-colors ${location.pathname === link.to ? 'text-mint' : 'text-navy'
                  }`}>
                  {link.label}
                </span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${location.pathname === link.to ? 'bg-mint text-white' : 'bg-gray-100 text-gray-400 group-hover:bg-mint/10 group-hover:text-mint'
                  }`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            ))}

            <Link
              to="/emergency"
              className="group flex items-center justify-between p-4 rounded-xl bg-coral/5 hover:bg-coral/10 border border-coral/10 transition-all mt-4 active:scale-[0.98]"
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="text-xl font-bold font-title text-coral flex items-center">
                <PhoneIcon className="w-5 h-5 mr-3" />
                {translations.nav.emergency}
              </span>
              <div className="w-8 h-8 rounded-full bg-coral/20 text-coral flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          </nav>

          {/* Footer Actions */}
          <div className="mt-8 space-y-6 pt-6 border-t border-gray-100">
            {/* Language Grid */}
            <div className="grid grid-cols-4 gap-2">
              {['de', 'en', 'es', 'ru'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => changeLanguage(lang as any)}
                  className={`py-3 rounded-lg border text-xs font-bold transition-all uppercase ${language === lang
                    ? 'border-mint bg-mint/10 text-navy shadow-sm'
                    : 'border-gray-100 bg-white text-gray-400'
                    }`}
                >
                  {lang}
                </button>
              ))}
            </div>

            <Button as={Link} to="/kontakt?booking=true" variant="primary" fullWidth size="lg" className="shadow-premium py-4 text-lg">
              {translations.nav.book}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;